import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import CancelIcon from '@mui/icons-material/Cancel'; // Misaligned
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Aligned
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'; // Example Icon for ToC
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Emergent
import InsightsIcon from '@mui/icons-material/Insights';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { AnalysisLayout } from './layout';

// Helper to safely parse JSON results
const safeParseResults = (results) => {
  if (!results) return { data: null, error: 'Results data is missing.' };
  try {
    const parsedData = typeof results === 'string' ? JSON.parse(results) : results;
    return { data: parsedData, error: null };
  } catch (error) {
    console.error('Error parsing ToCAlignmentAnalysis results:', error);
    return { data: null, error: `Failed to parse results: ${error.message}` };
  }
};

// Helper to get icon and color key based on classification
const getClassificationStyleKey = (classification) => {
  switch (classification) {
    case 'aligned':
      return {
        icon: <CheckCircleIcon fontSize="small" color="success" />,
        colorKey: 'success.main',
      };
    case 'misaligned':
      return { icon: <CancelIcon fontSize="small" color="error" />, colorKey: 'error.main' };
    case 'emergent':
      return { icon: <HelpOutlineIcon fontSize="small" color="info" />, colorKey: 'info.main' };
    default:
      return { icon: null, colorKey: 'text.secondary' };
  }
};

const TocAlignmentAnalysis = ({ item }) => {
  // Get the theme object
  const theme = useTheme();

  if (!item) {
    return <Alert severity="warning">Analysis item data is missing.</Alert>;
  }

  const { analysis_lens_type, results: rawResults } = item;
  const { data: results, error: parseError } = safeParseResults(rawResults);

  if (parseError) {
    return <Alert severity="error">{parseError}</Alert>;
  }

  if (!results) {
    return <Alert severity="warning">Parsed analysis results are empty.</Alert>;
  }

  // Destructure safely with defaults
  const {
    transcripts_analyzed = 0,
    total_aligned_elements = 0,
    total_misaligned_elements = 0,
    total_emergent_elements = 0,
    element_prevalence = [],
    priority_implications = [],
  } = results;

  const renderQuotes = (quotes) => {
    if (!quotes || quotes.length === 0) return null;
    return (
      <List dense disablePadding sx={{ pl: 2, mt: 1, borderLeft: '2px solid #eee' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          Representative Quotes:
        </Typography>
        {quotes.map((quote, index) => (
          <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
            <ListItemText
              primaryTypographyProps={{
                variant: 'caption',
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              {quote}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <AnalysisLayout
      results={results}
      item={{ ...item, name: 'Theory-of-Change Alignment Analysis' }}
    >
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <ChecklistRtlIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Theory-of-Change Alignment Analysis
            {analysis_lens_type && (
              <AnalysisLensHelperText lensName={analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />

        {/* Counts Section */}
        <Grid container spacing={2} sx={{ mb: 3, textAlign: 'center' }}>
          <Grid item xs={3}>
            <Typography variant="h4">{transcripts_analyzed}</Typography>
            <Typography variant="caption">Transcripts Analyzed</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" color="success.main">
              {total_aligned_elements}
            </Typography>
            <Typography variant="caption">Aligned Elements</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" color="error.main">
              {total_misaligned_elements}
            </Typography>
            <Typography variant="caption">Misaligned Elements</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h4" color="info.main">
              {total_emergent_elements}
            </Typography>
            <Typography variant="caption">Emergent Elements</Typography>
          </Grid>
        </Grid>

        <Divider />

        {/* Element Prevalence Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Element Prevalence ({element_prevalence.length})
          </Typography>
          {element_prevalence.length > 0 ? (
            element_prevalence.map((elem, index) => {
              const { icon, colorKey } = getClassificationStyleKey(elem.classification);
              // Resolve the color key using the theme
              let resolvedColor = theme.palette.text.secondary; // Default
              try {
                // Attempt to resolve nested color keys like 'success.main'
                const keys = colorKey.split('.');
                resolvedColor = keys.reduce((obj, key) => obj[key], theme.palette);
              } catch (e) {
                console.warn(`Could not resolve theme color key: ${colorKey}`);
              }

              return (
                <Accordion
                  key={index}
                  variant="outlined"
                  sx={{ borderLeft: `4px solid ${resolvedColor}`, mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {icon && (
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>{icon}</ListItemIcon>
                      )}
                      <Typography sx={{ flexGrow: 1, mr: 1 }}>
                        {elem.element || 'Unnamed Element'}
                      </Typography>
                      <Chip
                        label={`${elem.classification || 'unknown'} (${elem.prevalence || 0})`}
                        size="small"
                        sx={{
                          // Use the resolved color with alpha()
                          backgroundColor: alpha(resolvedColor, 0.1),
                          color: resolvedColor,
                          fontWeight: 'medium',
                        }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>{renderQuotes(elem.representative_quotes)}</AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              No element prevalence data found.
            </Typography>
          )}
        </Box>

        {/* Priority Implications Section */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <InsightsIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'primary.main' }} />
              Priority Insights
            </Typography>
            {priority_implications.length > 0 ? (
              <List dense>
                {priority_implications.map((imp, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                      <InsightsIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={imp} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No priority implications identified.
              </Typography>
            )}
          </CardContent>
        </Card>

        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
};

TocAlignmentAnalysis.propTypes = {
  /** The analysis item object containing results and metadata */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    analysis_lens_type: PropTypes.string,
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or parsed object
  }),
};

export default TocAlignmentAnalysis;
